import { useContext, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertVariant, IMAGE_TYPE, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../constants/constants';
import { _S3ImageResponse, _ToastMessageProps } from '../../../constants/staticTypes';
import withSidebar from '../../../hoc/withSideBar/withSideBar';
import logger from '../../../utils/logger';
import {
    BRANDING,
    CANCEL,
    CUSTOMIZED,
    DEFAULT,
    LOGO,
    PREVIEW,
    PRIMARY_COLOR,
    SECONDARY_COLOR,
    SETTINGS,
    TERTIARY_COLOR,
    TEXT_COLOR,
    THEME_MANAGEMENT,
    UPDATE,
    UPLOAD_MOBILE_LOGO,
    UPLOAD_WEB_LOGO,
    UPLOAD_DIMENSION_MSG,
    ORG_SETTINGS_PAGE_INFO,
    APPLY_TO,
} from '../../../constants/strings';
import { useTranslation } from '../../../hooks/useTranslation';
import { ORG_SETTINGS } from '../../../constants/queryKeys';
import './orgSettings.scss';
import MlxBlockUI from '../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxButton from '../../../components/common/MlxButton/MlxButton';
import MlxImageUpload from '../../../components/common/MlxImageUpload/MlxImageUpload';
import MlxToolTip from '../../../components/common/MlxToolTip/MlxToolTip';
import { _UpdateSettingsData } from '../../../helpers/venueSettingsHelper';
import { defaultToastData } from '../../../helpers/commonHelper';
import MlxToast from '../../../components/common/MlxToast/MlxToast';
import HttpServiceHelper from '../../../helpers/httpServiceHelper';
import { checkActionSubPermission } from '../../../utils/common';
import { AuthCtx } from '../../../context/AuthCtxProvider';
import OrgThemeSelection from './components/OrgThemeSelection';
import './components/OrgThemeSelection.scss';
import { getOrgSettings, updateOrgSettings } from '../../../helpers/orgSettingsHelper';

const languageTexts = {
    SETTINGS: SETTINGS,
    BRANDING: BRANDING,
    LOGO: LOGO,
    UPLOAD_WEB_LOGO: UPLOAD_WEB_LOGO,
    UPLOAD_MOBILE_LOGO: UPLOAD_MOBILE_LOGO,
    CANCEL: CANCEL,
    UPDATE: UPDATE,
    THEME_MANAGEMENT: THEME_MANAGEMENT,
    DEFAULT: DEFAULT,
    CUSTOMIZED: CUSTOMIZED,
    PRIMARY_COLOR: PRIMARY_COLOR,
    SECONDARY_COLOR: SECONDARY_COLOR,
    TERTIARY_COLOR: TERTIARY_COLOR,
    TEXT_COLOR: TEXT_COLOR,
    PREVIEW: PREVIEW,
    UPLOAD_DIMENSION_MSG: UPLOAD_DIMENSION_MSG,
    ORG_SETTINGS_PAGE_INFO: ORG_SETTINGS_PAGE_INFO,
    APPLY_TO: APPLY_TO,
};

function OrgSettings() {
    const queryClient = useQueryClient();
    const translatedText = useTranslation(languageTexts, 'none');
    const updateOrgSettingsMutation = useMutation(HttpServiceHelper);
    const { userType } = useContext(AuthCtx);

    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const [isDefaultTheme, setIsDefaultTheme] = useState<boolean>(false);
    const [customColors, setCustomColors] = useState<any>({});

    const [webLogo, setWebLogo] = useState<_S3ImageResponse>({
        imageKey: null,
        imageUrl: null, // need to get the image url using key
    });
    const [mobileLogo, setMobileLogo] = useState<_S3ImageResponse>({
        imageKey: null,
        imageUrl: null, // need to get the image url using key
    });

    const orgSettingsQuery = useQuery({
        queryKey: ORG_SETTINGS,
        queryFn: () => getOrgSettings(queryClient),
        onSuccess: (response) => {
            const responseData = response?.data?.data;
            setWebLogo({
                imageKey: responseData?.webLogoPhotoKey,
                imageUrl: responseData?.webLogo,
            });
            setMobileLogo({
                imageKey: responseData?.mobileLogoPhotoKey,
                imageUrl: responseData?.mobileLogo,
            });
            if (responseData?.primaryColor === '#fbaa07' && responseData?.secondaryColor === '#262930') {
                setIsDefaultTheme(true);
                setCustomColors({
                    primary: '#00b551',
                    secondary: '#262930',
                });
            } else {
                setIsDefaultTheme(false);
                setCustomColors({
                    primary: responseData?.primaryColor,
                    secondary: responseData?.secondaryColor === '' ? '#262930' : responseData?.secondaryColor,
                });
            }
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
        enabled: true,
    });

    const onSuccess = (data: any) => {
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        orgSettingsQuery.refetch();
    };

    const onError = (error: string, variant: string) => {
        setToastMessage({
            message: error,
            variant,
            show: true,
        });
    };

    const handleSettingsUpdate = () => {
        let data: _UpdateSettingsData = {
            id: localStorage.getItem('X-Organization-Id') || undefined,
            webLogo: webLogo.imageKey ?? '',
            mobileLogo: mobileLogo.imageKey ?? '',
            primaryColor: isDefaultTheme ? '#fbaa07' : customColors.primary,
            secondaryColor: isDefaultTheme ? '#262930' : customColors.secondary,
        };

        updateOrgSettings(updateOrgSettingsMutation, data, onSuccess, onError);
    };

    return (
        <MlxBlockUI isLoading={orgSettingsQuery.isLoading || orgSettingsQuery.isFetching} opacity={0.5}>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <div className="page-section h-100">
                <span className="page-header d-flex justify-content-between">
                    <span>{translatedText.SETTINGS}</span>
                </span>
                <div className="page-content overflow-auto m-0">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{translatedText.BRANDING}</span>
                        <div className="header-fields"></div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-auto" style={{ padding: '1rem' }}>
                        <div className="org-settings-page-info mb-4">
                            <i className="pi pi-info-circle me-2"></i>
                            {translatedText.ORG_SETTINGS_PAGE_INFO}
                        </div>
                        <div className="settings-logo-section">
                            <div className="settings-header">{translatedText.LOGO}</div>
                            <div className="step4-container-form custom-logo-upload">
                                <div className="d-flex flex-column me-3">
                                    <div className="d-flex align-items-center">
                                        <span className="mb-1">{translatedText.UPLOAD_WEB_LOGO}</span>
                                        <MlxToolTip target={'.uploadWebLogoInfo'}>
                                            <i
                                                className={`pi pi-info-circle mb-1 ms-2 text-primary uploadWebLogoInfo`}
                                                data-pr-tooltip={`${translatedText.UPLOAD_DIMENSION_MSG}: ${IMAGE_TYPE['WEB_LOGO'].width}x${IMAGE_TYPE['WEB_LOGO'].height}`}
                                                data-pr-position="left"
                                            ></i>
                                        </MlxToolTip>
                                    </div>
                                    <MlxImageUpload
                                        mode="basic"
                                        image={webLogo}
                                        setImage={setWebLogo}
                                        validateImageDimension={IMAGE_TYPE['WEB_LOGO']}
                                        cropImage={true}
                                    />
                                </div>
                                <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center">
                                        <span className="mb-1">{translatedText.UPLOAD_MOBILE_LOGO}</span>
                                        <MlxToolTip target={'.uploadMobileLogoInfo'}>
                                            <i
                                                className={`pi pi-info-circle mb-1 ms-2 text-primary uploadMobileLogoInfo`}
                                                data-pr-tooltip={`${translatedText.UPLOAD_DIMENSION_MSG}: ${IMAGE_TYPE['MOBILE_LOGO'].width}x${IMAGE_TYPE['MOBILE_LOGO'].height}`}
                                                data-pr-position="left"
                                            ></i>
                                        </MlxToolTip>
                                    </div>
                                    <MlxImageUpload
                                        mode="basic"
                                        image={mobileLogo}
                                        setImage={setMobileLogo}
                                        validateImageDimension={IMAGE_TYPE['MOBILE_LOGO']}
                                        cropImage={true}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="settings-theme-section">
                            <div className="settings-header mb-4">{translatedText.THEME_MANAGEMENT}</div>
                            <OrgThemeSelection
                                isDefaultTheme={isDefaultTheme}
                                setIsDefaultTheme={setIsDefaultTheme}
                                customColors={customColors}
                                setCustomColors={setCustomColors}
                            />
                        </div>
                    </div>
                </div>
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(
                        queryClient,
                        OrgPermissionKeys.MANAGE_VENUE_SETTINGS,
                        OrgSubPermissionKeys.EDIT_VENUE_SETTINGS
                    )) && (
                    <div className="settings-footer">
                        {/* <MlxButton
                        label={translatedText.CANCEL}
                        onClick={(event) => {
                            // event.preventDefault();
                            // setResetKey((prev) => prev + 1);
                        }}
                        icon="ri-close-large-line"
                        iconPos="right"
                        size="small"
                        outlined
                    /> */}
                        <MlxButton
                            className="ms-2"
                            label={translatedText.UPDATE}
                            onClick={(event) => {
                                event.preventDefault();
                                handleSettingsUpdate();
                            }}
                            icon="ri-check-line"
                            iconPos="right"
                            size="small"
                            disabled={updateOrgSettingsMutation?.isLoading}
                            loading={updateOrgSettingsMutation?.isLoading}
                        />
                    </div>
                )}
            </div>
        </MlxBlockUI>
    );
}

export default withSidebar(OrgSettings);
